import { useState, useMemo } from 'react';
import { DataTable } from '../ui/DataTable';
import { Card } from '../ui/Card';
import { FileText, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import type { JournalEntry } from '../../types/accounting';
import { useCurrency } from '../../hooks/useCurrency';

interface LedgerViewProps {
  accountId: string;
  entries: JournalEntry[];
  loading?: boolean;
  /** Nom du compte pour l'affichage */
  accountName?: string;
  /** Code du compte */
  accountCode?: string;
}

// Type pour les lignes de table avec id obligatoire
type LedgerTableRow = JournalEntry & { 
  id: string;
  lineDebit: number;
  lineCredit: number;
  lineBalance: number;
};

export function LedgerView({ 
  accountId, 
  entries, 
  loading,
  accountName,
  accountCode 
}: LedgerViewProps) {
  const { formatConverted } = useCurrency();

  // Protéger contre les entrées undefined
  const safeEntries = Array.isArray(entries) ? entries : [];

  // Calcul du solde cumulé et des totaux
  const { totalDebit, totalCredit, runningBalance, tableData } = useMemo(() => {
    let totalDebit = 0;
    let totalCredit = 0;
    let cumulativeBalance = 0;
    
    const data: LedgerTableRow[] = safeEntries.map(entry => {
      let lineDebit = 0;
      let lineCredit = 0;
      
      entry.lines?.forEach(line => {
        if (line.accountId === accountId) {
          lineDebit += Number(line.debit) || 0;
          lineCredit += Number(line.credit) || 0;
        }
      });
      
      totalDebit += lineDebit;
      totalCredit += lineCredit;
      cumulativeBalance += (lineDebit - lineCredit);

      return {
        ...entry,
        lineDebit,
        lineCredit,
        lineBalance: cumulativeBalance
      };
    });

    return {
      totalDebit,
      totalCredit,
      runningBalance: totalDebit - totalCredit,
      tableData: data
    };
  }, [safeEntries, accountId]);
  
  const columns = [
    {
      header: 'Date',
      accessor: (entry: LedgerTableRow) => {
        const date = new Date(entry.date);
        return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
      },
      width: 100,
      sortable: true
    },
    {
      header: 'N° Pièce',
      accessor: (entry: LedgerTableRow) => (
        <span className="font-mono text-xs">{entry.reference || '-'}</span>
      ),
      width: 110,
      sortable: true
    },
    {
      header: 'Libellé',
      accessor: (entry: LedgerTableRow) => entry.description || '-',
      minWidth: 200
    },
    {
      header: 'Débit',
      accessor: (entry: LedgerTableRow) => entry.lineDebit > 0 ? (
        <span className="text-green-600 dark:text-green-400">{formatConverted(entry.lineDebit)}</span>
      ) : '-',
      className: 'text-right font-mono',
      width: 130,
      sortable: true
    },
    {
      header: 'Crédit',
      accessor: (entry: LedgerTableRow) => entry.lineCredit > 0 ? (
        <span className="text-red-600 dark:text-red-400">{formatConverted(entry.lineCredit)}</span>
      ) : '-',
      className: 'text-right font-mono',
      width: 130,
      sortable: true
    },
    {
      header: 'Solde',
      accessor: (entry: LedgerTableRow) => (
        <span className={entry.lineBalance >= 0 ? 'text-gray-900 dark:text-white' : 'text-red-600 dark:text-red-400'}>
          {formatConverted(entry.lineBalance)}
        </span>
      ),
      className: 'text-right font-mono font-medium',
      width: 130,
      sortable: true
    }
  ];

  return (
    <Card 
      title={
        <div className="flex items-center gap-3">
          <FileText className="h-5 w-5 text-primary" />
          <div>
            <span className="font-semibold">Mouvements du compte</span>
            {accountCode && (
              <span className="ml-2 text-xs font-mono bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                {accountCode}
              </span>
            )}
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Résumé rapide */}
        {!loading && safeEntries.length > 0 && (
          <div className="grid grid-cols-3 gap-4 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
            <div className="text-center">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Total Débit</p>
              <p className="text-sm font-semibold text-green-600 dark:text-green-400 flex items-center justify-center gap-1">
                <TrendingUp className="h-3 w-3" />
                {formatConverted(totalDebit)}
              </p>
            </div>
            <div className="text-center border-x border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Total Crédit</p>
              <p className="text-sm font-semibold text-red-600 dark:text-red-400 flex items-center justify-center gap-1">
                <TrendingDown className="h-3 w-3" />
                {formatConverted(totalCredit)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Solde</p>
              <p className={`text-sm font-semibold flex items-center justify-center gap-1 ${
                runningBalance >= 0 ? 'text-gray-900 dark:text-white' : 'text-red-600 dark:text-red-400'
              }`}>
                <Minus className="h-3 w-3" />
                {formatConverted(runningBalance)}
              </p>
            </div>
          </div>
        )}

        {/* Tableau des mouvements */}
        <DataTable
          columns={columns}
          data={tableData}
          loading={loading}
          emptyMessage="Aucun mouvement trouvé pour ce compte"
          emptyTitle="Aucun mouvement"
          showToolbar={true}
          showPagination={true}
          pageSize={15}
          enableResize={true}
          enableSort={true}
          enableFilters={true}
          enableColumnToggle={true}
          enableGlobalFilter={true}
          storageKey={`ledger-view-${accountId}`}
          maxHeight="400px"
          density="compact"
        />
      </div>
    </Card>
  );
}